import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import PawPrint from './PawPrint';
import { LogOut, LayoutDashboard, PawPrint as PawPrintIcon, Scan, MessageSquare, BarChart3, Gift, CreditCard, Settings } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/cat-profiles', label: 'My Cats', icon: PawPrintIcon },
  { path: '/ingredient-checker', label: 'Scan Food', icon: Scan },
  { path: '/behavior-chat', label: 'Chat', icon: MessageSquare },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/affiliates', label: 'Products', icon: Gift },
  { path: '/subscription', label: 'Plan', icon: CreditCard },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const DashboardLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9]">
      {/* Top Nav (Floating) */}
      <nav className="fixed top-3 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-[1200px] bg-white/95 backdrop-blur-md border border-gray-200 rounded-2xl shadow-xl shadow-gray-200/50 px-4 sm:px-6" data-testid="navbar">
        <div className="flex justify-between items-center h-14">
          <Link to="/dashboard" className="flex items-center gap-2">
            <PawPrint className="w-7 h-7" color="#BFDB38" />
            <span className="text-lg font-extrabold text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>PurrCheck</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-[#1B4332] text-white shadow-sm'
                      : 'text-[#1B4332]/60 hover:bg-[#1B4332]/5 hover:text-[#1B4332]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-3.5 h-3.5" strokeWidth={2} />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* User Section */}
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 bg-[#1B4332]/5 rounded-lg px-3 py-1.5">
              <div className="w-6 h-6 bg-[#1B4332] text-white rounded-full flex items-center justify-center text-[11px] font-bold">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <span className="text-[13px] font-semibold text-[#1B4332]">{user?.full_name}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="rounded-lg w-8 h-8 hover:bg-red-50 hover:text-red-500 transition-all duration-200"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={2} />
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="lg:hidden flex gap-1 overflow-x-auto pb-2 pt-1 scrollbar-hide -mx-1 px-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? 'bg-[#1B4332] text-white'
                    : 'text-[#1B4332]/50 bg-[#1B4332]/5'
                }`}
              >
                <item.icon className="w-3 h-3" strokeWidth={2} />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Spacer for Floating Nav */}
      <div className="h-28 lg:h-20" />

      {/* Main Content */}
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {title && (
          <h1
            className="text-lg sm:text-xl font-extrabold text-[#1B4332] mb-4 sm:mb-5 tracking-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="page-title"
          >
            {title}
          </h1>
        )}
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
